import React from 'react';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Confirm step for approving/rejecting several POs at once.
export default function POBulkDecisionDialog({ decision, pos, onClose, onConfirm }) {
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const approve = decision === 'approved';
  const total = (pos || []).reduce((s, p) => s + Number(p.total || 0), 0);

  React.useEffect(() => { if (decision) setNote(''); }, [decision]);

  const submit = async () => {
    setBusy(true);
    await onConfirm(note);
    setBusy(false);
  };

  return (
    <Dialog open={!!decision} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="po-bulk-decision-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1a1a2e]">{approve ? 'Approve' : 'Reject'} {pos.length} PO{pos.length !== 1 ? 's' : ''}?</DialogTitle>
          <DialogDescription>
            Total {inr(total)}. The submitter and approvers will be notified. Selling prices stay as they are.
          </DialogDescription>
        </DialogHeader>
        <ul className="max-h-56 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl" data-testid="po-bulk-decision-list">
          {pos.map((p) => (
            <li key={p._id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="font-semibold text-[#1a1a2e]">{p.po_number}</p>
                <p className="text-[11px] text-slate-500 truncate">{p.vendor_name || '—'} · {p.client_name || 'No client'}</p>
              </div>
              <span className="tabular-nums font-semibold">{inr(p.total)}</span>
            </li>
          ))}
        </ul>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
          placeholder="Optional note, applied to every selected PO" data-testid="po-bulk-decision-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="po-bulk-decision-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className={approve ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
            data-testid="po-bulk-decision-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : approve ? <CheckCircle2 className="w-4 h-4 mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
            {approve ? 'Approve' : 'Reject'} {pos.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
